import { isHTTPError, isNetworkError, isTimeoutError } from 'ky'
import { z } from 'zod'

import { createAppError, type AppError } from './app-error'

/**
 * The only file that knows what a backend error body looks like. When the
 * backend changes its error envelope, this schema is the single edit.
 */
const fieldMessages = z.record(z.string(), z.union([z.string(), z.array(z.string())]))

const errorBodySchema = z.looseObject({
  code: z.string().optional(),
  message: z.string().optional(),
  detail: z.string().optional(),
  fields: fieldMessages.optional(),
  errors: fieldMessages.optional(),
  request_id: z.string().optional(),
  requestId: z.string().optional(),
  error: z
    .union([
      z.string(),
      z.looseObject({
        code: z.string().optional(),
        message: z.string().optional(),
        fields: fieldMessages.optional(),
        /**
         * The envelope's free-form slot. Today the backend fills it with the
         * raw binding error text — a developer artefact, not user copy — so
         * only the map form is promoted to per-field messages.
         */
        details: z.union([z.string(), fieldMessages]).optional(),
      }),
    ])
    .optional(),
})

const firstMessage = (value: string | string[]) => (Array.isArray(value) ? (value[0] ?? '') : value)

const flattenFields = (fields: z.infer<typeof fieldMessages> | undefined) =>
  fields === undefined
    ? undefined
    : Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, firstMessage(value)]))

/** Raw 5xx text may contain stack traces or connection strings — never shown. */
const isServerError = (status: number) => status >= 500

export function normalizeError(error: unknown): AppError {
  if (isHTTPError(error)) {
    const { response } = error
    const status = response.status
    const headerRequestId = response.headers.get('x-request-id') ?? undefined

    // ky consumes the body to populate `error.data`, so `response.json()` is
    // no longer available — `data` is the only way to read it.
    const body = errorBodySchema.safeParse(error.data)
    const parsed = body.success ? body.data : undefined
    const nested = typeof parsed?.error === 'object' ? parsed.error : undefined
    const flatMessage = typeof parsed?.error === 'string' ? parsed.error : undefined
    const textBody = typeof error.data === 'string' ? error.data : undefined

    const code = parsed?.code ?? nested?.code
    const nestedDetails = typeof nested?.details === 'object' ? nested.details : undefined
    const fields = flattenFields(
      parsed?.fields ?? parsed?.errors ?? nested?.fields ?? nestedDetails,
    )
    const message =
      parsed?.message ??
      nested?.message ??
      flatMessage ??
      parsed?.detail ??
      textBody ??
      response.statusText

    return createAppError({
      status,
      code: code ?? (isServerError(status) ? 'SERVER_ERROR' : undefined),
      message: isServerError(status) ? 'Server error' : message,
      fields,
      requestId: parsed?.request_id ?? parsed?.requestId ?? headerRequestId,
    })
  }

  // ky's own timeout, or `AbortSignal.timeout` on the 401 replay.
  if (isTimeoutError(error) || (error instanceof DOMException && error.name === 'TimeoutError')) {
    return createAppError({ status: 0, code: 'TIMEOUT', message: 'Request timed out' })
  }

  if (isNetworkError(error)) {
    return createAppError({ status: 0, code: 'OFFLINE', message: 'Network unavailable' })
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return createAppError({ status: 0, code: 'ABORTED', message: 'Request aborted' })
  }

  // A schema mismatch lands here: the request succeeded but the payload was
  // not what the app models. It must surface, never be silently tolerated.
  if (error instanceof z.ZodError) {
    console.error('response did not match its schema', error.issues)
    return createAppError({ status: 0, code: 'INVALID_RESPONSE', message: 'Unexpected response' })
  }

  return createAppError({
    status: 0,
    code: 'UNKNOWN',
    message: error instanceof Error ? error.message : 'Unknown error',
  })
}
